import { useTranslation } from 'react-i18next'
import { Download } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  FORM_CONTROL_FOCUS_CLASS,
  FORM_FILTER_HOVER_CLASS,
} from '@/components/ui/form-control-styles'
import { Input } from '@/components/ui/input'
import { Permit } from '@/components/guards/Permit'
import { MultiSelectFilter } from '@/components/ui/multi-select-filter'
import { DateRangeFilter } from '@/components/clients/tabs/DateRangeFilter'
import { TopbarSearchIcon } from '@/components/icons/topbar'
import { cn } from '@/lib/utils'

const STATUS_OPTIONS: { value: string; labelKey: string }[] = [
  { value: 'draft', labelKey: 'list.status.draft' },
  { value: 'pending_signature', labelKey: 'list.status.pendingSignature' },
  { value: 'signed', labelKey: 'list.status.signed' },
  { value: 'declined', labelKey: 'list.status.declined' },
  { value: 'active', labelKey: 'list.status.active' },
  { value: 'to_be_renewed', labelKey: 'list.status.toBeRenewed' },
  { value: 'suspended', labelKey: 'list.status.suspended' },
  { value: 'terminated', labelKey: 'list.status.terminated' },
  { value: 'expired', labelKey: 'list.status.expired' },
]

// Figma 3396:160462 — toolbar control chrome (search / selects / export).
const controlBorder = 'border border-[#e7e9ef] bg-white'
const filterTriggerClass = cn(
  'h-10 w-auto shrink-0 justify-between gap-2 rounded-xl py-3 pl-4 pr-3',
  'text-sm font-normal leading-[1.4] text-[#171a20]',
  controlBorder,
  'data-[state=open]:ring-0 [&_svg]:size-4 [&_svg]:opacity-100 [&_svg]:text-[#272c36]',
  FORM_CONTROL_FOCUS_CLASS,
  FORM_FILTER_HOVER_CLASS
)

export interface ContractListFiltersProps {
  search: string
  onSearchChange: (value: string) => void
  statuses: string[]
  endDateFrom?: string
  endDateTo?: string
  onFiltersChange: (next: {
    status?: string
    end_date_from?: string
    end_date_to?: string
  }) => void
  onExport: () => void
  className?: string
}

// Contracts list toolbar (Figma 3396:160462): Search → All Status → Due Date
// on the left, Export on the right.
export function ContractListFilters({
  search,
  onSearchChange,
  statuses,
  endDateFrom,
  endDateTo,
  onFiltersChange,
  onExport,
  className,
}: ContractListFiltersProps) {
  const { t } = useTranslation('contracts')

  return (
    <div
      className={cn(
        'flex shrink-0 flex-wrap items-center justify-between gap-3',
        className
      )}
    >
      <div className='flex min-w-0 flex-wrap items-center gap-3'>
        <div className='relative w-full min-w-50 max-w-full sm:w-[320px]'>
          <TopbarSearchIcon className='pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#272c36]' />
          <Input
            value={search}
            onChange={e => onSearchChange(e.target.value)}
            placeholder={t('list.searchPlaceholder')}
            maxLength={100}
            className={cn(
              'h-10 w-full rounded-xl py-3 pl-10 pr-4 text-sm font-normal leading-[1.4] text-[#171a20]',
              'placeholder:text-[#3d434e]',
              controlBorder,
              FORM_CONTROL_FOCUS_CLASS,
              FORM_FILTER_HOVER_CLASS
            )}
          />
        </div>

        <MultiSelectFilter
          options={STATUS_OPTIONS.map(o => ({
            value: o.value,
            label: t(o.labelKey),
          }))}
          selected={statuses}
          onChange={values =>
            onFiltersChange({
              status: values.length ? values.join(',') : undefined,
            })
          }
          placeholder={t('list.filters.statusPlaceholder')}
          selectedLabel={count => t('list.filters.statusSelected', { count })}
          clearLabel={t('list.filters.allStatuses')}
          emptyLabel={t('list.filters.statusEmpty')}
          triggerClass={filterTriggerClass}
        />

        <DateRangeFilter
          appearance='field'
          label={t('list.filters.dueDate')}
          from={endDateFrom}
          to={endDateTo}
          triggerClassName={cn(
            'h-10 gap-2 rounded-xl border-[#e7e9ef] py-3 pl-4 pr-3 text-sm font-normal leading-[1.4] text-[#171a20]',
            '[&_svg]:text-[#272c36]',
            FORM_CONTROL_FOCUS_CLASS
          )}
          onChange={({ date_from, date_to }) =>
            onFiltersChange({
              end_date_from: date_from,
              end_date_to: date_to,
            })
          }
        />
      </div>

      <Permit module='contracts' action='view'>
        <Button
          type='button'
          variant='outline'
          className={cn(
            'h-10 shrink-0 gap-2 rounded-xl px-4 py-3 text-sm font-normal leading-[1.4] text-[#171a20]',
            controlBorder,
            'hover:bg-accent'
          )}
          onClick={onExport}
        >
          <Download className='size-4 text-[#272c36]' strokeWidth={1.6} />
          {t('list.export')}
        </Button>
      </Permit>
    </div>
  )
}

export default ContractListFilters
